import { create } from 'zustand';
import { Node, Edge } from '@xyflow/react';
import { DialogueData, DialogueSection } from '../types/dialogue';
import { usePersistenceStore } from './persistenceStore';

interface DialogueStore {
  dialogueData: DialogueData | null;
  nodes: Node[];
  edges: Edge[];
  selectedNode: string | null;
  isLoading: boolean;
  error: string | null;
  setDialogueData: (data: DialogueData) => void;
  setNodes: (nodes: Node[]) => void;
  setEdges: (edges: Edge[]) => void;
  setSelectedNode: (nodeId: string | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  updateDialogueSection: (key: string, section: DialogueSection) => void;
  addDialogueSection: (key: string, section: DialogueSection) => void;
  removeDialogueSection: (key: string) => void;
  exportToJSON: () => string;
  importFromJSON: (jsonString: string) => Promise<void>;
  generateGraphFromDialogue: () => void;
}

export const useDialogueStore = create<DialogueStore>((set, get) => ({
  dialogueData: null,
  nodes: [],
  edges: [],
  selectedNode: null,
  isLoading: false,
  error: null,

  setDialogueData: (data) => {
    set({ dialogueData: data });
    usePersistenceStore.getState().setDirty(true);
  },
  setNodes: (nodes) => {
    set({ nodes });
    usePersistenceStore.getState().setDirty(true);
  },
  setEdges: (edges) => {
    set({ edges });
    usePersistenceStore.getState().setDirty(true);
  },
  setSelectedNode: (nodeId) => set({ selectedNode: nodeId }),
  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),

  updateDialogueSection: (key, section) => {
    const { dialogueData } = get();
    if (!dialogueData) return;

    set({
      dialogueData: {
        ...dialogueData,
        dialogues: {
          ...dialogueData.dialogues,
          [key]: section
        }
      }
    });
    usePersistenceStore.getState().setDirty(true);
  },

  addDialogueSection: (key, section) => {
    const { dialogueData } = get();
    if (!dialogueData) return;

    set({
      dialogueData: {
        ...dialogueData,
        dialogues: {
          ...dialogueData.dialogues,
          [key]: section
        }
      }
    });
    usePersistenceStore.getState().setDirty(true);
  },

  removeDialogueSection: (key) => {
    const { dialogueData } = get();
    if (!dialogueData) return;

    const { [key]: removed, ...rest } = dialogueData.dialogues;
    set({
      dialogueData: {
        ...dialogueData,
        dialogues: rest
      }
    });
    usePersistenceStore.getState().setDirty(true);
  },

  exportToJSON: () => {
    const { dialogueData } = get();
    if (!dialogueData) return '{}';
    return JSON.stringify(dialogueData, null, 2);
  },

  importFromJSON: async (jsonString) => {
    set({ isLoading: true, error: null });
    try {
      const data = JSON.parse(jsonString) as DialogueData;
      // Basic validation
      if (!data.dialogues || typeof data.dialogues !== 'object') {
        throw new Error('Invalid dialogue format: missing dialogues object');
      }
      set({ dialogueData: data, error: null });
      get().generateGraphFromDialogue();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to import JSON';
      set({ error: errorMessage });
      console.error('Failed to import JSON:', error);
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  generateGraphFromDialogue: () => {
    const { dialogueData } = get();
    if (!dialogueData) return;

    const nodes: Node[] = [];
    const edges: Edge[] = [];
    let yOffset = 0;

    // Create nodes for each dialogue section
    Object.entries(dialogueData.dialogues).forEach(([key, section], index) => {
      const dialogueNodeId = `dialogue-${key}`;
      
      // Main dialogue node
      nodes.push({
        id: dialogueNodeId,
        type: 'dialogue',
        data: {
          label: key,
          dialogueKey: key,
          section: section
        },
        position: { x: 100, y: yOffset }
      });

      let xOffset = 400;
      
      // Create nodes for each condition
      section.conditions.forEach((condition, condIndex) => {
        const conditionNodeId = `${dialogueNodeId}-cond-${condIndex}`;
        
        nodes.push({
          id: conditionNodeId,
          type: 'condition',
          data: {
            label: condition.condition,
            condition: condition
          },
          position: { x: xOffset, y: yOffset + condIndex * 150 }
        });

        // Connect dialogue to condition
        edges.push({
          id: `edge-${dialogueNodeId}-${conditionNodeId}`,
          source: dialogueNodeId,
          target: conditionNodeId,
          type: 'animated',
          label: `Condition ${condIndex + 1}`,
          animated: true
        });

        // If condition has dialogue options, create nodes for them
        if (condition.dialogue_options) {
          condition.dialogue_options.forEach((option, optIndex) => {
            const optionNodeId = `${conditionNodeId}-opt-${optIndex}`;
            
            nodes.push({
              id: optionNodeId,
              type: 'response',
              data: {
                label: option.display,
                option: option
              },
              position: { x: xOffset + 300, y: yOffset + condIndex * 150 + optIndex * 40 }
            });

            // Connect condition to option
            edges.push({
              id: `edge-${conditionNodeId}-${optionNodeId}`,
              source: conditionNodeId,
              target: optionNodeId,
              type: 'custom',
              style: { stroke: '#888' }
            });

            // Connect option to target dialogue if it exists
            if (dialogueData.dialogues[option.topic_key]) {
              edges.push({
                id: `edge-${optionNodeId}-dialogue-${option.topic_key}`,
                source: optionNodeId,
                target: `dialogue-${option.topic_key}`,
                label: option.topic_key,
                style: { stroke: '#4CAF50', strokeDasharray: '5 5' }
              });
            }
          });
        }
      });

      // Default response node
      const defaultNodeId = `${dialogueNodeId}-default`;
      nodes.push({
        id: defaultNodeId,
        type: 'response',
        data: {
          label: 'Default Response',
          isDefault: true,
          default: section.default
        },
        position: { x: xOffset, y: yOffset + section.conditions.length * 150 + 50 }
      });

      edges.push({
        id: `edge-${dialogueNodeId}-${defaultNodeId}`,
        source: dialogueNodeId,
        target: defaultNodeId,
        label: 'Default',
        style: { stroke: '#FFA500', strokeDasharray: '10 5' }
      });

      yOffset += Math.max(300, (section.conditions.length + 1) * 150 + 100);
    });

    set({ nodes, edges });
  }
}));